import React, { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useThemeColors } from '../../hooks/useThemeColors';
import { config } from '../../config/config';

// Simple inline SVG sparkline
const Sparkline = ({ data = [], color = '#10b981', width = 96, height = 32 }) => {
  if (!data || data.length === 0) {
    return <div style={{ width, height }} className="opacity-40 flex items-center justify-center text-[10px] text-gray-400">no data</div>;
  }

  const min = Math.min(...data);
  const max = Math.max(...data);
  const range = max - min || 1;
  const step = width / Math.max(1, data.length - 1);

  const points = data
    .map((v, i) => {
      const x = i * step;
      const y = height - ((v - min) / range) * (height - 4) - 2;
      return `${x},${y}`;
    })
    .join(' ');

  const gradientId = `sparkline-${color.replace(/[^a-zA-Z0-9]/g, '')}`;
  const areaPoints = `0,${height} ${points} ${width},${height}`;

  return (
    <svg width={width} height={height} className="overflow-visible">
      <defs>
        <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={color} stopOpacity="0.22" />
          <stop offset="100%" stopColor={color} stopOpacity="0" />
        </linearGradient>
      </defs>
      <polygon fill={`url(#${gradientId})`} points={areaPoints} />
      <polyline
        fill="none"
        stroke={color}
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        points={points}
      />
    </svg>
  );
};

const StatCard = ({ label, value, change, sparklineData, sparklineColor, iconBg, iconColor, icon, loading }) => {
  return (
    <div className="bg-white rounded-2xl border border-gray-100 shadow-sm hover:shadow-md transition-shadow duration-200 px-4 py-3.5 flex items-center justify-between gap-3 min-w-0">
      <div className="flex items-start gap-3 min-w-0 flex-1">
        <div
          className="flex items-center justify-center w-10 h-10 rounded-xl shrink-0"
          style={{ backgroundColor: iconBg }}
        >
          <div style={{ color: iconColor }}>{icon}</div>
        </div>
        <div className="min-w-0">
          <p className="text-[11px] font-medium text-gray-500 mb-0.5 truncate">{label}</p>
          <p className="text-xl font-bold text-gray-900 leading-tight">
            {loading ? <span className="inline-block w-10 h-5 bg-gray-100 animate-pulse rounded" /> : (value?.toLocaleString?.() ?? value ?? 0)}
          </p>
          {change && !loading && (
            <p className="text-[10px] text-gray-400 mt-0.5 truncate">{change}</p>
          )}
        </div>
      </div>
      <div className="shrink-0">
        <Sparkline data={sparklineData} color={sparklineColor} />
      </div>
    </div>
  );
};

// Build a sparkline (last 14 days) from leads using createdAt
const sparkFromCreatedAt = (items, predicate = () => true, days = 14) => {
  const buckets = new Array(days).fill(0);
  const now = new Date();
  const startOfDay = (d) => {
    const x = new Date(d);
    x.setHours(0, 0, 0, 0);
    return x.getTime();
  };
  const todayStart = startOfDay(now);
  (items || []).forEach((it) => {
    if (!predicate(it)) return;
    const t = it?.createdAt ? startOfDay(it.createdAt) : null;
    if (!t) return;
    const diffDays = Math.round((todayStart - t) / (24 * 60 * 60 * 1000));
    if (diffDays >= 0 && diffDays < days) {
      buckets[days - 1 - diffDays] += 1;
    }
  });
  // If literally nothing, return empty so the component shows "no data"
  return buckets.some((v) => v > 0) ? buckets : [];
};

const fetchLeadCount = async (params) => {
  try {
    const token = localStorage.getItem('token');
    const q = new URLSearchParams({ page: '1', limit: '1', ...params }).toString();
    const res = await fetch(`${config.apiUrl}/api/leads?${q}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!res.ok) return 0;
    const data = await res.json();
    return data?.pagination?.total ?? 0;
  } catch (_) {
    return 0;
  }
};

const LeadStatsCards = ({ leads = [], totalCount = 0 }) => {
  const { buttonColors } = useThemeColors();
  const { user } = useSelector((state) => state.auth);

  const [serverCounts, setServerCounts] = useState({
    total: null,
    waiting: null,
    recentlySent: null,
    converted: null,
  });
  const [countsLoading, setCountsLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setCountsLoading(true);
    Promise.all([
      fetchLeadCount({ trackingStatus: 'all_active' }),
      fetchLeadCount({ trackingStatus: 'all_waiting' }),
      fetchLeadCount({ trackingStatus: 'recently_sent' }),
      fetchLeadCount({ trackingStatus: 'converted' }),
    ])
      .then(([total, waiting, recentlySent, converted]) => {
        if (cancelled) return;
        setServerCounts({ total, waiting, recentlySent, converted });
      })
      .finally(() => { if (!cancelled) setCountsLoading(false); });
    return () => { cancelled = true; };
    // Refresh when user changes (e.g. login)
  }, [user?._id]);

  const stats = useMemo(() => {
    const safeLeads = Array.isArray(leads) ? leads : [];
    const total = serverCounts.total ?? totalCount ?? 0;
    const waiting = serverCounts.waiting ?? 0;
    const recentlySent = serverCounts.recentlySent ?? 0;
    const converted = serverCounts.converted ?? 0;

    const pctOf = (n) =>
      total > 0 ? `${((n / total) * 100).toFixed(1)}% of total` : '0% of total';

    // Build sparklines from currently loaded leads (best-effort, real data)
    return {
      total: {
        value: total,
        change: '100% of all leads',
        spark: sparkFromCreatedAt(safeLeads),
      },
      waiting: {
        value: waiting,
        change: pctOf(waiting),
        spark: sparkFromCreatedAt(
          safeLeads,
          (l) => l?.trackingStatus === 'waiting' || l?.trackingStatus === 'critical'
        ),
      },
      recentlySent: {
        value: recentlySent,
        change: pctOf(recentlySent),
        spark: sparkFromCreatedAt(safeLeads, (l) => l?.trackingStatus === 'recently_sent'),
      },
      converted: {
        value: converted,
        change: pctOf(converted),
        spark: sparkFromCreatedAt(safeLeads, (l) => l?.trackingStatus === 'converted'),
      },
    };
  }, [leads, totalCount, serverCounts]);

  // Card configs — neutral pastel icon backgrounds work with all theme colors
  const cards = [
    {
      label: 'Total Leads',
      value: stats.total.value,
      change: stats.total.change,
      sparklineData: stats.total.spark,
      sparklineColor: '#10b981',
      iconBg: '#ECFDF5',
      iconColor: '#10b981',
      icon: (
        <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
          <circle cx="9" cy="8" r="3.5" />
          <path d="M2.5 20a6.5 6.5 0 0113 0" />
          <circle cx="17" cy="9" r="2.5" />
          <path d="M15 20a4.5 4.5 0 016.5-4" />
        </svg>
      )
    },
    {
      label: 'Waiting For Proposal',
      value: stats.waiting.value,
      change: stats.waiting.change,
      sparklineData: stats.waiting.spark,
      sparklineColor: '#f59e0b',
      iconBg: '#FFFBEB',
      iconColor: '#f59e0b',
      icon: (
        <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
          <circle cx="12" cy="12" r="9" />
          <path d="M12 7v5l3 2" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      )
    },
    {
      label: 'Recently Sent',
      value: stats.recentlySent.value,
      change: stats.recentlySent.change,
      sparklineData: stats.recentlySent.spark,
      sparklineColor: '#8b5cf6',
      iconBg: '#F5F3FF',
      iconColor: '#8b5cf6',
      icon: (
        <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
          <path d="M22 2L11 13" />
          <path d="M22 2l-7 20-4-9-9-4 20-7z" />
        </svg>
      )
    },
    {
      label: 'Converted',
      value: stats.converted.value,
      change: stats.converted.change,
      sparklineData: stats.converted.spark,
      sparklineColor: buttonColors?.primary || '#10b981',
      iconBg: '#ECFDF5',
      iconColor: buttonColors?.primary || '#10b981',
      icon: (
        <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="12" cy="12" r="9" />
          <path d="M8 12l3 3 5-6" />
        </svg>
      )
    }
  ];

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-3">
      {cards.map((c, idx) => (
        <StatCard key={idx} {...c} loading={countsLoading} />
      ))}
    </div>
  );
};

export default LeadStatsCards;
